import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { ProjectionIcon } from '@/feature/lobby/components/ProjectionIcon';
import { ProjectionType } from '@/types/api.generated';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

export type ValueChartModalRef = {
    show: () => void;
};

const modalId = 'valueChart';

const CHART_HEIGHT = 396;
const VERTICAL_AXIS = 292;
const HORIZONTAL_AXIS = 256;
const HALF_VERTICAL_AXIS = VERTICAL_AXIS / 2;
const HALF_HORIZONTAL_AXIS = HORIZONTAL_AXIS / 2;
const ICON_WIDTH = 30;
const ICON_HEIGHT = 20;
const HORIZONTAL_PADDING = 14;
const VERTICAL_PADDING = 16;

const STEP_X = ICON_WIDTH - 2; // how far right each icon moves per level
const STEP_Y = ICON_HEIGHT + VERTICAL_PADDING; // how far up or down each icon moves per level

type ChartPoint = { left: number; top: number; projectionType: ProjectionType };

const boostPoint = (level: number): Pick<ChartPoint, 'left' | 'top'> => ({
    left: HALF_HORIZONTAL_AXIS + HORIZONTAL_PADDING + STEP_X * (level - 1),
    top: HALF_VERTICAL_AXIS - STEP_Y * level,
});

const edgePoint = (level: number): Pick<ChartPoint, 'left' | 'top'> => ({
    left: HALF_HORIZONTAL_AXIS - ICON_WIDTH - HORIZONTAL_PADDING - STEP_X * (level - 1),
    top: HALF_VERTICAL_AXIS + VERTICAL_PADDING + STEP_Y * (level - 1),
});

const CHART_POINTS: ChartPoint[] = [
    { ...boostPoint(4), projectionType: ProjectionType.Boosted4 },
    { ...boostPoint(3), projectionType: ProjectionType.SuperBoosted },
    { ...boostPoint(2), projectionType: ProjectionType.Boosted },
    { ...boostPoint(1), projectionType: ProjectionType.MiniBoosted },
    { ...edgePoint(1), projectionType: ProjectionType.Edge1 },
    { ...edgePoint(2), projectionType: ProjectionType.Edge2 },
    { ...edgePoint(3), projectionType: ProjectionType.Edge3 },
    { ...edgePoint(4), projectionType: ProjectionType.Edge4 },
];

export const ValueChartModal = React.forwardRef<ValueChartModalRef, {}>((_props, ref) => {
    const { t } = useTranslation('common');
    const modalRef = useRef<BottomSheetModal>(null);
    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current?.present();
        },
    }));

    return (
        <Modal id={modalId} modalRef={modalRef} backgroundColor={ds.colorValue.background.neutral.base}>
            <Box paddingHorizontal={'s24'} paddingTop={'s24'} paddingBottom={'s20'} gap={'s8'}>
                <Text variant={'headlineSmall'} color={ds.colorToken.text.primary} textAlign={'center'}>
                    Value Chart
                </Text>
                <Text variant={'bodyMedium'} color={ds.colorToken.text.secondary} textAlign={'center'}>
                    This chart helps display how Rocket and Edge pick levels affect difficulty and multiplier potential.
                </Text>
            </Box>

            <Box height={CHART_HEIGHT} width={'100%'}>
                {/* Axis labels */}
                <Text
                    variant={'labelMedium'}
                    color={ds.colorToken.text.secondary}
                    textAlign={'center'}
                    style={styles.higherLabel}
                >
                    Higher Multiplier
                </Text>
                <Text
                    variant={'labelMedium'}
                    color={ds.colorToken.text.secondary}
                    textAlign={'center'}
                    style={styles.lowerLabel}
                >
                    Lower Multiplier
                </Text>
                <Text variant={'labelMedium'} color={ds.colorToken.text.secondary} style={styles.easierLabel}>
                    Easier
                </Text>
                <Text variant={'labelMedium'} color={ds.colorToken.text.secondary} style={styles.harderLabel}>
                    Harder
                </Text>
                <Box width={HORIZONTAL_AXIS} height={VERTICAL_AXIS} style={styles.chart}>
                    <Box style={styles.verticalAxis} />
                    <Box style={styles.horizontalAxis} />

                    {CHART_POINTS.map(point => (
                        <Box key={point.projectionType} style={[styles.point, { left: point.left, top: point.top }]}>
                            <ProjectionIcon selectedProjection={{ type: point.projectionType }} showLevels />
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box paddingHorizontal={'s16'} paddingTop={'s16'}>
                <Button label={t('close')} hierarchy={'secondary'} onPress={() => modalRef.current?.dismiss()} />
            </Box>
        </Modal>
    );
});

const styles = StyleSheet.create({
    chart: {
        alignSelf: 'center',
        top: 52,
    },
    verticalAxis: {
        position: 'absolute',
        left: '50%',
        width: 2,
        height: VERTICAL_AXIS,
        borderRadius: 2,
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    horizontalAxis: {
        position: 'absolute',
        top: '50%',
        width: HORIZONTAL_AXIS,
        height: 2,
        borderRadius: 2,
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    higherLabel: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 24,
    },
    lowerLabel: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: CHART_HEIGHT - 24 - 16, // chart height - distance from axis - label height
    },
    easierLabel: {
        position: 'absolute',
        left: 16,
        top: CHART_HEIGHT / 2 - 8, // half chart height - half label height
    },
    harderLabel: {
        position: 'absolute',
        right: 16,
        top: CHART_HEIGHT / 2 - 8, // half chart height - half label height
    },
    point: {
        position: 'absolute',
    },
});
